import React, { KeyboardEvent } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { PutCallRight } from '../../../models/FlowOfFunds/flow-of-funds.model';
import { EM_DASH, PUT_CALL_LABELS } from '../../../utils/flowOfFundsFormat';
import { ConsiderationFieldKind } from '../../../utils/flow-of-funds/consideration-field-format';

const UNSET_OPTION_VALUE = '';
const TEXT_FIELD_MAX_LENGTH = 500;

const YES_NO_OPTIONS: [string, string][] = [
    [UNSET_OPTION_VALUE, EM_DASH],
    ['true', 'Yes'],
    ['false', 'No'],
];

const PUT_CALL_OPTIONS: [string, string][] = Object.entries(PUT_CALL_LABELS).map(
    ([value, label]) => [value, label]
);

const NUMBER_INPUT_KINDS = ['percent', 'multiple', 'ratio', 'days', 'months', 'integer'];

const KIND_INPUT_WIDTH_CLASS: Partial<Record<ConsiderationFieldKind, string>> = {
    days: 'ff-editable-cell__input--days',
    boolean: 'ff-editable-cell__input--boolean',
    putCallRight: 'ff-editable-cell__input--put-call',
};

const inputClassName = (kind: ConsiderationFieldKind): string =>
    ['form-control', 'ff-editable-cell__input', KIND_INPUT_WIDTH_CLASS[kind]]
        .filter(Boolean)
        .join(' ');

const optionsForKind = (kind: ConsiderationFieldKind): [string, string][] | null => {
    if (kind === 'boolean') {
        return YES_NO_OPTIONS;
    }
    if (kind === 'putCallRight') {
        return PUT_CALL_OPTIONS;
    }
    return null;
};

const defaultOptionValueForKind = (kind: ConsiderationFieldKind): string =>
    kind === 'putCallRight' ? String(PutCallRight.Na) : UNSET_OPTION_VALUE;

export interface EditableConsiderationCellInputProps {
    kind: ConsiderationFieldKind;
    draft: string;
    onDraftChange: (value: string) => void;
    onCommit: (value: string) => void;
    onFocus: () => void;
    onBlur: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export const EditableConsiderationCellInput = ({
    kind,
    draft,
    onDraftChange,
    onCommit,
    onFocus,
    onBlur,
    onKeyDown,
}: EditableConsiderationCellInputProps) => {
    if (kind === 'currency') {
        return (
            <CurrencyInput
                className={inputClassName(kind)}
                value={draft}
                allowDecimals
                decimalsLimit={2}
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                onFocus={onFocus}
                onValueChange={(nextValue) => onDraftChange(nextValue ?? '')}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
            />
        );
    }

    const options = optionsForKind(kind);
    if (options) {
        return (
            <select
                className={inputClassName(kind)}
                value={draft || defaultOptionValueForKind(kind)}
                onChange={(event) => {
                    onDraftChange(event.target.value);
                    onCommit(event.target.value);
                }}>
                {options.map(([value, label]) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </select>
        );
    }

    const isNumberInput = NUMBER_INPUT_KINDS.includes(kind);
    return (
        <input
            type={isNumberInput ? 'number' : 'text'}
            className={inputClassName(kind)}
            value={draft}
            maxLength={isNumberInput ? undefined : TEXT_FIELD_MAX_LENGTH}
            onFocus={onFocus}
            onChange={(event) => onDraftChange(event.target.value)}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
        />
    );
};
